import React, { useEffect, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ShieldCheck,
  UserRoundPlus,
  ShoppingBag,
  PackageCheck,
  Heart,
} from "lucide-react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { getErrorMessage } from "../../core/api.jsx";
import { sendLoginOtp, verifyLoginOtp } from "../../core/services/authService";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import storeLogo from "../../assets/images/logo.png";

export default function Register() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, setSession } = useAuth();
  const { notify } = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState(location.state?.phone || "");
  const [sponsorId, setSponsorId] = useState(() =>
    new URLSearchParams(location.search).get("sponsorId")?.trim().toUpperCase() || "",
  );
  const otpPending = Boolean(location.state?.otpPending);
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("details");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const returnTo = location.state?.from || "/profile";

  useEffect(() => {
    if (location.state?.phone) setPhone(location.state.phone);
    const referralSponsorId = new URLSearchParams(location.search)
      .get("sponsorId")
      ?.trim()
      .toUpperCase();
    if (referralSponsorId) setSponsorId(referralSponsorId);
  }, [location.search, location.state]);
  if (isAuthenticated) return <Navigate to={returnTo} replace />;

  const requestOtp = async (event) => {
    event.preventDefault();
    const digits = phone.replace(/\D/g, "");
    if (name.trim().length < 2)
      return setError("Enter your name (at least 2 characters).");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
      return setError("Enter a valid email address.");
    if (digits.length < 10)
      return setError("Enter a valid 10-digit mobile number.");
    const originalPendingPhone = String(location.state?.phone || "").replace(
      /\D/g,
      "",
    );
    if (otpPending && digits === originalPendingPhone) {
      setPhone(digits);
      setStep("otp");
      setError("");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const data = await sendLoginOtp(digits);
      if (!data.isNewUser) {
        navigate("/login", {
          replace: true,
          state: { phone: digits, otpPending: true, from: returnTo },
        });
        notify(
          "An account already exists for this number. Please sign in.",
          "error",
        );
        return;
      }
      setPhone(digits);
      setStep("otp");
    } catch (requestError) {
      setError(getErrorMessage(requestError, "We couldn't send your code."));
    } finally {
      setBusy(false);
    }
  };

  const resendOtp = async () => {
    setBusy(true);
    setError("");
    try {
      const data = await sendLoginOtp(phone.replace(/\D/g, ""));
      if (!data.isNewUser) {
        navigate("/login", {
          replace: true,
          state: { phone, otpPending: true, from: returnTo },
        });
        notify(
          "An account already exists for this number. Please sign in.",
          "error",
        );
        return;
      }
      notify("A new verification code has been sent.");
    } catch (requestError) {
      setError(getErrorMessage(requestError, "We couldn't send your code."));
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (event) => {
    event.preventDefault();
    if (otp.trim().length !== 6)
      return setError("Enter the 6-digit code we sent you.");
    setBusy(true);
    setError("");
    try {
      const data = await verifyLoginOtp({
        phone,
        otp: otp.trim(),
        name: name.trim(),
        email: email.trim(),
        sponsorId: sponsorId.trim(),
        isRegistration: true,
        registerFrom: "web",
      });
      setSession(data.token, data.user);
      notify(
        `Welcome to Sugam Store, ${data.user?.name?.split(" ")[0] || name.trim()}!`,
      );
      navigate(returnTo, { replace: true });
    } catch (requestError) {
      setError(
        getErrorMessage(requestError, "That code didn't work. Try again."),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="auth-page page-shell">
      <section className="auth-layout auth-layout--register">
        <aside className="auth-story">
          <div className="auth-story__top"><img src={storeLogo} alt="Sugam Store" /><span><ShieldCheck size={15} /> Secure account</span></div>
          <div className="auth-story__content">
            <span className="auth-story__icon"><ShoppingBag size={25} /></span>
            <p className="auth-story__kicker">WELCOME TO SUGAM</p>
            <h2>A little more<br />personal from here.</h2>
            <p>Create an account to save your details, follow deliveries and keep your favourite picks handy.</p>
            <div className="auth-story__perks"><span><PackageCheck size={17} /> Easy order updates</span><span><Heart size={17} /> Your wishlist, always</span></div>
          </div>
          <span className="auth-story__foot">Your next favourite is just around the corner.</span>
        </aside>
        <div className="auth-card">
        <div className="auth-card__brand">
          <img src={storeLogo} alt="Sugam Store" />
          <span className="auth-card__seal">
            <UserRoundPlus size={21} />
          </span>
        </div>
        {step === "details" ? (
          <>
            <p className="eyebrow">Make yourself at home</p>
            <h1>
              Create your
              <br />
              Sugam account.
            </h1>
            <p className="auth-card__copy">
              A few details, then your one-time code. Your account keeps orders
              and saved finds together.
            </p>
            <form className="form-stack" onSubmit={requestOtp}>
              <label className="field-label" htmlFor="register-name">
                Your name
              </label>
              <input
                className="text-field"
                id="register-name"
                autoComplete="name"
                maxLength={30}
                placeholder="Full name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
              />
              <label className="field-label" htmlFor="register-email">
                Email address
              </label>
              <input
                className="text-field"
                id="register-email"
                type="email"
                autoComplete="email"
                placeholder="name@example.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
              <label className="field-label" htmlFor="register-phone">
                Mobile number
              </label>
              <div className="phone-field">
                <span>+91</span>
                <input
                  id="register-phone"
                  inputMode="tel"
                  autoComplete="tel-national"
                  maxLength={12}
                  placeholder="98765 43210"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  required
                />
              </div>
              <label className="field-label" htmlFor="register-sponsor-id">
                Sponsor ID (optional)
              </label>
              <input
                className="text-field"
                id="register-sponsor-id"
                autoComplete="off"
                maxLength={40}
                placeholder="Enter sponsor's member ID"
                value={sponsorId}
                onChange={(event) => setSponsorId(event.target.value.toUpperCase())}
              />
              {error && <p className="form-error">{error}</p>}
              <button
                className="button button--primary button--full"
                disabled={busy}
              >
                {busy ? (
                  "Sending code…"
                ) : (
                  <>
                    {otpPending ? "Continue to verification" : "Create account"}{" "}
                    <ArrowRight size={17} />
                  </>
                )}
              </button>
            </form>
            <p className="auth-card__switch">
              Already have an account?{" "}
              <Link to="/login" state={{ phone, from: returnTo }}>
                Sign in
              </Link>
            </p>
          </>
        ) : (
          <>
            <button
              className="text-button auth-back"
              onClick={() => {
                setStep("details");
                setError("");
              }}
            >
              <ArrowLeft size={15} /> Edit your details
            </button>
            <p className="eyebrow">Verify your number</p>
            <h1>
              One small step
              <br />
              to get started.
            </h1>
            <p className="auth-card__copy">
              We sent a 6-digit code to <strong>+91 {phone}</strong>. Your
              account will be created after verification.
            </p>
            <form className="form-stack" onSubmit={verifyOtp}>
              <label className="field-label" htmlFor="register-otp">
                Verification code
              </label>
              <input
                className="text-field otp-field"
                id="register-otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="• • • • • •"
                value={otp}
                onChange={(event) =>
                  setOtp(event.target.value.replace(/\D/g, ""))
                }
              />
              {error && <p className="form-error">{error}</p>}
              <button
                className="button button--primary button--full"
                disabled={busy}
              >
                {busy ? (
                  "Creating account…"
                ) : (
                  <>
                    Verify & create account <Check size={17} />
                  </>
                )}
              </button>
            </form>
            <button
              className="text-button auth-resend"
              disabled={busy}
              onClick={resendOtp}
            >
              Didn't receive a code? Send again
            </button>
          </>
        )}
        <Link to="/shop" className="auth-card__back-shop">
          <ArrowLeft size={14} /> Continue shopping
        </Link>
        </div>
      </section>
    </main>
  );
}
